import { useEffect } from "react";
import { verifyAuthToken } from "../../../services/authService";
import { fetchProduct } from "../../../services/shopifyService";
import { useDispatch } from "react-redux";
import { setProfile } from "../../../redux/slice/profileSlice";
import { setSession } from "../../../redux/slice/authSlice";
import LoadingFallback from "../../../components/elements/LoadingFallback";
import { useNavigate } from "react-router-dom";
import { loadUserData, determineUserDestination } from "../../../util/navigationUtils";
import { API_BASE_URL } from "../../../config/apiConfig";

/**
 * Client /auth/callback (SPA route, app host).
 *
 * Secure Shopify login always hits the API first: GET https://api.../auth/callback?… (HMAC + replay).
 * This page only handles:
 * - Option B: full signed query on the client host → redirect to API (misconfiguration self-heal)
 * - Existing session: auth_token already in localStorage (cookie bootstrap from index.js) → verify + continue
 * - Otherwise: fail closed (no JWT minting from customerId alone)
 */
const ShopifyCallback = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleShopifyCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const customerId = params.get("customerId");
      const email = params.get("email");
      const hmac = params.get("hmac");
      const timestamp = params.get("timestamp");
      const nonce = params.get("nonce");

      const hasFullSignedHandoff =
        !!customerId &&
        !!email &&
        !!hmac &&
        !!timestamp &&
        !!nonce;

      if (hasFullSignedHandoff) {
        const apiOrigin = new URL(API_BASE_URL).origin;
        window.location.replace(
          `${apiOrigin}/auth/callback?${params.toString()}`
        );
        return;
      }

      const token = localStorage.getItem("auth_token");
      if (token) {
        try {
          const body = await verifyAuthToken();
          if (!body || body.status !== 200 || !body.data) {
            navigate("/signin?error=shopify_auth_failed", { replace: true });
            return;
          }

          const authUser = body.data;
          const accessToken = localStorage.getItem("auth_token");

          dispatch(setProfile({ ...authUser, access_token: accessToken }));
          dispatch(
            setSession({
              user: { ...authUser, access_token: accessToken },
              status: "authorized",
              error: null,
            })
          );

          const productId = params.get("productId") || params.get("planId");
          const variantId = params.get("variantId");

          if (productId) {
            try {
              await handleProductSelection(productId, variantId);
            } catch (error) {
              console.error("Error fetching product:", error);
            }
          }

          let userData;
          try {
            userData = await loadUserData(authUser._id, dispatch, false);
          } catch (error) {
            // Signed in fine; only their books didn't load. The user area retries the load
            // and redirects once it succeeds, so don't treat this as a failed sign-in.
            console.error("Loading user data after sign-in failed:", error);
            navigate("/dashboard", { replace: true });
            return;
          }
          const { profile, myBooks, activeBookId } = userData;

          const destination = determineUserDestination(
            profile,
            myBooks,
            activeBookId
          );
          navigate(destination, { replace: true });
          return;
        } catch (error) {
          console.error("Authentication failed:", error);
          navigate("/signin?error=shopify_auth_failed", { replace: true });
          return;
        }
      }

      navigate("/signin?error=shopify_insecure_handoff", { replace: true });
    };

    handleShopifyCallback();
  }, [dispatch, navigate]);

  return <LoadingFallback />;
};

/**
 * Handles fetching and storing product/plan selection
 */
const handleProductSelection = async (productId, variantId) => {
  localStorage.removeItem("selectedPlan");

  const product = await fetchProduct(`gid://shopify/Product/${productId}`);

  if (!product?.variants?.edges?.length) {
    throw new Error("No variants available for product");
  }

  let selectedVariant;

  if (variantId) {
    const variantEdge = product.variants.edges.find(
      (edge) =>
        edge.node.id === variantId ||
        edge.node.id === `gid://shopify/ProductVariant/${variantId}`
    );
    selectedVariant = variantEdge?.node;

    if (!selectedVariant) {
      console.warn(`Variant ${variantId} not found, using first variant`);
      selectedVariant = product.variants.edges[0]?.node;
    }
  } else {
    selectedVariant = product.variants.edges[0]?.node;
  }

  if (!selectedVariant) {
    throw new Error("No variant could be selected");
  }

  const price = parseFloat(selectedVariant.price.amount);
  const planObj = {
    id: selectedVariant.id,
    productId: product.id,
    name: product.title,
    description: product.description,
    highLightTag: 0,
    highLightText: "Most Popular",
    planPricingData: {
      price,
      priceDescription: "Select Plan",
    },
    features: [],
    shopifyProductId: product.id,
    shopifyVariantId: selectedVariant.id,
    image: product.images.edges[0]?.node?.url || null,
  };

  localStorage.setItem("selectedPlan", JSON.stringify(planObj));
  window.dispatchEvent(new Event("cartUpdated"));
  console.log("Plan saved:", planObj.name);

  return planObj;
};

export default ShopifyCallback;
